import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import clsx from 'clsx'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import CalendarPriceBlurb from './CalendarPriceBlurb'
import { min } from 'lib/array/arrayUtils'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { INTL_LONG_MONTH } from 'constants/dateFormats'
import { getDateFromMonthKey, useIntlDateFormatter } from 'lib/datetime/dateUtils'

const Root = styled.div`
  padding-bottom: ${rem(32)};
`

const CalendarWrapper = styled.div`
  &:not(:first-child) {
    margin-top: ${rem(24)};
  }
`
const YearWrapper = styled.div`
  display: grid;
  margin-top: ${rem(20)};
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-gap: ${rem(20)};
    grid-template-columns: repeat(4, 1fr);
  }
`

const MonthTile = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  text-align: start;
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: ${props => props.theme.shadow.bottom.medium};
      }
    }
    &:focus {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

const BodyData = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
  width: 100%;
  height: ${rem(60)};
`

const MainSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  margin-top: ${rem(8)};
`

interface Props {
  rooms?: Array<App.Occupants>
  className?: string
  selectedDay?: App.BedbankCalendarDay
  duration: number
  adultsNumber: number
  fetching: boolean
  flights: boolean
  displayPricingAsPerPerson: boolean
  onMonthSelect: (monthKey: string) => void
  monthsByYear: Map<string, Array<App.BedbankCalendarMonth>>
}

function CalendarMonthPicker(props: Props) {
  const {
    className,
    monthsByYear,
    onMonthSelect,
    duration,
    adultsNumber,
    selectedDay,
    fetching,
    flights,
    displayPricingAsPerPerson,
    rooms,
  } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const onMonthClick = useCallback((monthIndex: string) => {
    onMonthSelect(monthIndex)
  }, [onMonthSelect])

  const keyData = flights ? 'cheapestDayWithFlights' : 'cheapestDay'

  const yearCheapestDay = useMemo(() => {
    return Array.from(monthsByYear.entries()).reduce<Record<string, App.BedbankCalendarDay | undefined>>((acc, [year, months]) => {
      acc[year] = min(months.filter(d => d[keyData]), d => {
        const hotelPrice = d[keyData]?.price ?? 0
        const propertyFees = d[keyData]?.propertyFees ?? 0
        const hotelFlightPrice = d[keyData]?.flightPrice ?? 0
        return (hotelPrice + propertyFees) + (flights ? hotelFlightPrice : 0)
      })?.[keyData]
      return acc
    }, {})
  }, [monthsByYear, keyData, flights])

  return (
    <Root className={className}>
      {Array.from(monthsByYear.entries()).map(([year, months]) => {
        return <CalendarWrapper key={year}>
          <Heading variant="heading3">{year}</Heading>
          {!!yearCheapestDay[year] && !fetching && <CalendarPriceBlurb
            price={yearCheapestDay[year].price}
            propertyFees={yearCheapestDay[year].propertyFees}
            flightPrice={yearCheapestDay[year].flightPrice}
            displayPricingAsPerPerson={displayPricingAsPerPerson}
            adultsNumber={adultsNumber}
          />}
          <YearWrapper>
            {months.map((month) => {
              const hotelPrice = month[keyData]?.price ?? 0
              const propertyFees = month[keyData]?.propertyFees ?? 0
              const {
                total,
                saleUnit,
              } = getCalendarDisplayTotalPrice({
                price: hotelPrice + propertyFees,
                adultsNumber,
                nights: duration,
                roomsNumber: rooms?.length ?? 1,
                withFlights: !!flights,
                flightPrice: month[keyData]?.flightPrice ?? 0,
                displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
              })

              const nightsWithFlightsLabel = intl.formatMessage({
                id: 'iD90ExMh7cwUBqCHxOKK',
                defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
              }, { count: duration })
              const nightsOnlyLabel = intl.formatMessage({
                id: 'PL7L25rVJnTfF3y4xd5I',
                defaultMessage: '{count, plural, one {# night} other {# nights}} from',
              }, { count: duration })
              const nightsCaption = (flights && month[keyData]?.flightPrice) ? nightsWithFlightsLabel : nightsOnlyLabel
              const saleUnitWithFlightsLabel = intl.formatMessage({
                id: 'uiBE5J-zJj8oqAwzDBQw',
                defaultMessage: '{saleUnit} + flights',
              }, { saleUnit })
              const saleUnitLabel = (flights && !month[keyData]?.flightPrice) ? saleUnitWithFlightsLabel : saleUnit

              const monthName = dateFormatter(getDateFromMonthKey(month.key), INTL_LONG_MONTH)

              return <MonthTile
                className={clsx({ selected: selectedDay?.monthKey === month.key })}
                onClick={() => onMonthClick(month.key)}
                key={month.key}
              >
                <BodyData>
                  <div>
                    <Heading variant="heading6">{monthName}</Heading>
                    {fetching && <VerticalSpacer gap={4}>
                      <TextLoadingBox typography="caption-large" width="100%" />
                      <TextLoadingBox typography="body-large" width="100%" />
                    </VerticalSpacer>}
                    {!fetching && <MainSection>
                      {!month[keyData] && <Caption variant="large" colour="light-foreground-alt" underline>
                        {intl.formatMessage({
                          id: 'hgJJUFUUf4KL6tD3mXyt',
                          defaultMessage: 'View dates',
                        })}
                      </Caption>}
                      {month[keyData] && month.hasPromo &&
                        <div>
                          <Caption variant="medium" colour="light-foreground-alt">
                            {nightsCaption}
                          </Caption>
                          <FormattedMessage
                            id="3972QVAvywItxSSxpCLm"
                            defaultMessage="{priceBlock} {saleUnitBlock}"
                            
                            values={{
                              priceBlock: <BodyText as="span" variant="large" weight="bold">
                                <FormatCurrency value={total} />
                                {displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
                              </BodyText>,
                              saleUnitBlock: !displayPricingAsPerPerson
                                ? <Caption as="span" variant="medium" colour="light-foreground-alt">{saleUnitLabel}</Caption>
                                : null,
                            }}
                          />
                        </div>}
                    </MainSection>}
                  </div>
                  <div>
                    {month.hasPromo && <SolidFireIcon size="S" colour="urgency" />}
                  </div>
                </BodyData>
              </MonthTile>
            })}
          </YearWrapper>
        </CalendarWrapper>
      })}
    </Root>
  )
}

export default CalendarMonthPicker
